import { type FormEvent, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { getFetch, startFetch } from "../../api/client";
import type { Fetch } from "../../api/models";
import { StatusBadge } from "../../components/Badge";
import { usePoll } from "../../hooks/usePoll";
import { errorMessage } from "../../lib/format";

const isRunning = (f: Fetch | null) => f !== null && (f.status === "QUEUED" || f.status === "RUNNING");

/** Stays mounted while closed so a running fetch keeps being polled. */
export function FetchPanel({ open, onDone }: { open: boolean; onDone: () => void }) {
  const [form, setForm] = useState({ count: 20, minPage: 4000, maxPage: 12000, perPage: 2 });
  const [fetch, setFetch] = useState<Fetch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const poll = useCallback(() => {
    if (!fetch) return;
    getFetch(fetch.id)
      .then((f) => {
        setFetch(f);
        if (!isRunning(f)) onDone();
      })
      .catch((e) => setError(errorMessage(e)));
  }, [fetch, onDone]);

  usePoll(poll, 3000, isRunning(fetch));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setStarting(true);
    setError(null);
    try {
      setFetch(await startFetch(form));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const num = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: Number(e.target.value) });

  const lastLines = fetch ? fetch.log.trimEnd().split("\n").slice(-4).join("\n") : "";

  if (!open) return null;
  return (
    <form className="panel inline-form" onSubmit={submit}>
      <label className="field">
        count <input type="number" min={1} max={500} value={form.count} onChange={num("count")} />
      </label>
      <label className="field">
        min page <input type="number" min={1} value={form.minPage} onChange={num("minPage")} />
      </label>
      <label className="field">
        max page <input type="number" min={1} value={form.maxPage} onChange={num("maxPage")} />
      </label>
      <label className="field">
        per page <input type="number" min={1} max={15} value={form.perPage} onChange={num("perPage")} />
      </label>
      <button type="submit" className="primary" disabled={starting || isRunning(fetch)}>
        Start
      </button>
      {error && <span className="error">{error}</span>}
      {fetch && (
        <span>
          <StatusBadge status={fetch.status} /> fetch #{fetch.id}: {fetch.added}/{fetch.params.count} added,{" "}
          {fetch.pagesSeen} pages seen
          {fetch.runId != null && (
            <>
              {" · "}
              <Link to={`/runs/${fetch.runId}`}>run #{fetch.runId}</Link>
            </>
          )}
          {fetch.error && <span className="error"> {fetch.error}</span>}
        </span>
      )}
      {lastLines && (
        <pre className="log" style={{ flexBasis: "100%", margin: 0, maxHeight: 90 }}>
          {lastLines}
        </pre>
      )}
    </form>
  );
}
